import { FC } from "react";
import { SearchResultTableRow } from "../views/Pages/Portfolio/CompanySearch";
import { observer } from "mobx-react";
import { Box, VBox } from "../components-v2/Box";
import { isMobileView } from "../utils/screen";
import { MTextInput } from "../components-v2/TextInput";
import { Button } from "../components-v2/Button";
import { isFailure, isOk, isPending, Result } from "../common/common";
import { TMessage } from "../i18n/tt";
import { Dialog } from "../components-v2/Dialog";
import { SuggestedCompany } from "../types/CompanyInfo";
import { Alert } from "../components-v2/Alert";
import { OnboardingCandidateResponse } from "../stores/PortfolioStore";
import { Autocomplete } from "../components-v2/Autocomplete";
import { identity } from "lodash";
import { CompanySearchController } from "./SearchController";
import { getRoleTypeLabel } from "../types/enums/RoleType";
import { TMessageKey } from "../i18n/types.gen";

import { useTranslation } from "../stores/RootStoreContext";
import { Markdown } from "../text/Markdown";
import { StringFieldModel } from "../common/commonForms";

export const NorwegianCompanySearchDialog: FC<{
  controller: CompanySearchController;
}> = observer(({ controller }) => {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t("registerCompany.addCompany.header")}
      submitLabel={t("askForPermissionDialog.sendRequest")}
      onCancel={() => controller.onClose(isOk(controller.askForPermissionResult))}
      onSubmit={!isOk(controller.askForPermissionResult) ? controller.onSubmit : undefined}
      isSubmitting={isPending(controller.askForPermissionResult)}
      cancelLabel={t("close")}
      fullscreen={isMobileView()}
      maxWidth={"md"}
    >
      <VBox padding gap={"md"}>
        {!isOk(controller.askForPermissionResult) && (
          <>
            <p>{t("registerCompany.addCompany.body")}</p>
            <Autocomplete
              placeholder={t("registerCompany.searchLabel")}
              idFn={identity}
              items={controller.getSearchResult}
              inputValue={controller.companyNameSearchField.inputValue}
              labelFn={(company) => company.name}
              onSelect={(company) => controller.selectCompany(company)}
              onInputValueChange={(query) => controller.searchForCompany(query)}
              onClear={() => controller.companyNameSearchField.clear()}
            />
          </>
        )}
        {controller.selectedCompany && (
          <AskForPermissionForm
            company={controller.selectedCompany.name}
            askForPermissionResult={controller.askForPermissionResult}
            receiverEmailAddress={controller.receiverEmailAddress}
            onboardingCandidatesResult={controller.onboardingCandidatesResult}
            companyRegisterKey={getCompanyRegisterKey(controller.selectedCompany)}
          />
        )}
      </VBox>
    </Dialog>
  );
});

export const AskForPermissionForm: FC<{
  askForPermissionResult?: Result<TMessage, TMessage>;
  onboardingCandidatesResult?: Result<OnboardingCandidateResponse>;
  receiverEmailAddress: StringFieldModel;
  companyRegisterKey: TMessageKey;
  company: string;
}> = observer(
  ({
    askForPermissionResult,
    onboardingCandidatesResult,
    receiverEmailAddress,
    companyRegisterKey,
    company,
  }) => {
    const { t } = useTranslation();
    return (
      <VBox>
        {isFailure(onboardingCandidatesResult) ? (
          <Alert
            type={"info"}
            icon={false}
            message={
              <div>
                <Markdown
                  value={t("askForPermissionDialog.onboardingCandidatesResult.error", {
                    company: company,
                  })}
                />
              </div>
            }
          />
        ) : !isOk(askForPermissionResult) ? (
          <Alert
            type={"success"}
            icon={false}
            marginTop
            message={
              <div>
                {t("askForPermissionDialog.infoMessage", {
                  company: isOk(onboardingCandidatesResult)
                    ? onboardingCandidatesResult.data.companyName
                    : "",
                  companyRegister: t(companyRegisterKey),
                })}
              </div>
            }
          />
        ) : null}

        {!isOk(askForPermissionResult) &&
          isOk(onboardingCandidatesResult) &&
          onboardingCandidatesResult.data.namesWithRoles.map((it) => {
            return (
              <Box key={`${it.name}_${it.roleTypes}`}>
                {it.name} ({it.roleTypes.map((it) => t(getRoleTypeLabel(it))).join(", ")})
              </Box>
            );
          })}
        {!isOk(askForPermissionResult) && (
          <MTextInput
            label={t("askForPermissionDialog.email-to-ceo-or-chairperson")}
            model={receiverEmailAddress}
            autofocus={true}
          />
        )}
      </VBox>
    );
  },
);

export const SwedishCompanySearchDialog: FC<{
  controller: CompanySearchController;
}> = observer(({ controller }) => {
  const { t } = useTranslation();

  return (
    <Dialog
      title={t("portfolio.search-bolagsverket.title")}
      submitLabel={t("askForPermissionDialog.sendRequest")}
      onCancel={() => controller.onClose(isOk(controller.askForPermissionResult))}
      onSubmit={
        controller.selectedCompany && !isOk(controller.askForPermissionResult)
          ? controller.onSubmit
          : undefined
      }
      isSubmitting={isPending(controller.askForPermissionResult)}
      cancelLabel={t("close")}
      fullscreen={isMobileView()}
      maxWidth={"md"}
    >
      {!isOk(controller.askForPermissionResult) && (
        <VBox padding>
          <Box paddingBottom={"lg"}>{t("portfolio.search-bolagsverket.description")}</Box>
          <Box vertical={isMobileView()}>
            <MTextInput
              placeholder={t("portfolio.search-bolagsverket.search-placeholder")}
              model={controller.searchField}
            />
            <div>
              <Button
                variant={"primary"}
                type={"submit"}
                size={"sm"}
                label={t("search")}
                loading={isPending(controller.searchResult)}
                onClick={controller.searchSwedishBusinessRegister}
              />
            </div>
          </Box>
        </VBox>
      )}
      <VBox padding>
        {isOk(controller.searchResult) && controller.isRenderSearchResult && (
          <>
            <VBox padding>
              {t("portfolio.search-bolagsverket.result-count", {
                count: controller.searchResult.data.length,
              })}
            </VBox>

            {controller.searchResult.data.map((it) => {
              return (
                <SearchResultTableRow
                  key={it.name + it.organizationnumber}
                  company={it}
                  onSelect={() => controller.selectSwedishCompany(it)}
                />
              );
            })}
          </>
        )}
        {controller.tooManyHits && <div>{t("portfolio.search-bolagsverket.too-many-hits")}</div>}
        {isOk(controller.searchResult) && controller.searchResult.data.length === 0 && (
          <div>
            {t("portfolio.search-bolagsverket.no-hits", {
              searchQuery: controller.searchField.inputValue,
            })}
          </div>
        )}
        {controller.selectedCompany && (
          <AskForPermissionForm
            company={controller.selectedCompany.name}
            askForPermissionResult={controller.askForPermissionResult}
            receiverEmailAddress={controller.receiverEmailAddress}
            onboardingCandidatesResult={controller.onboardingCandidatesResult}
            companyRegisterKey={controller.companyRegisterKey}
          />
        )}
      </VBox>
    </Dialog>
  );
});

function getCompanyRegisterKey(company?: SuggestedCompany) {
  if (company?.sourceCountry === "SE") {
    return "askForPermissionDialog.bolagsverket";
  } else {
    return "askForPermissionDialog.brreg";
  }
}
